import React, { useState, useEffect } from "react";
import { _axiosCall } from "../../../../../../shared/helpers/apicalls";
import {
  _capitalizeText,
  _formatNumber,
  _formatPrice,
  _tranformTagsQuery,
  getCohortQuery,
  _activityFQ,
} from "../../../../../../shared/helpers/utils";
import {
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import moment from "moment";
import { getCurrentPeriod, getPriorPeriod, getRangeEnd, getRangeStart, generatePeriodNames, formatDateRange } from "../../../exportreport/utils";

var apiURL = process.env.API_ROOT;

// ── Percentage / comparison color constants ──
// Update these named constants to change all percentage colors site-wide in one place.
// TODO: COLOR_NEGATIVE is pure red (#FF0000) — may want a softer red for dark-bg contrast; change here to update everywhere
// TODO: COLOR_POSITIVE is pure green (#00FF00) — may want a softer green for dark-bg contrast; change here to update everywhere
var COLOR_POSITIVE = "#00FF00";
var COLOR_NEGATIVE = "#FF0000";
var COLOR_COMPARISON_TEXT = "#F4F4F2";

// ── Font constants ──
// TODO: Ensure "FreightNeo Pro Semi" is loaded via @font-face or a font service in the export/rendering environment
var FONT_HEADING = "'FreightNeo Pro Semi', 'Georgia', serif";
var FONT_BODY = "'Roboto', Arial, sans-serif";
// OpenType proportional lining figures — applied to all heading numbers
var FONT_FEATURE_LNUM = '"lnum", "pnum"';

// ── Colors ──
var C = {
  bg: "#0E2536",
  white: "#ffffff",
  text: "#ffffff",
  muted: "#ffffff",
  dim: "#ffffff",
  border: "rgba(255,255,255,0.06)",
  gridLine: "rgba(255,255,255,0.9)",
  red: "#e74c65",
  green: "#4cd19a",
  blue: "#3b82f6",
  orange: "#f59e42",
};

// ── Price tier colors (donut + sales volume table) ──
// $3M-$5M, $5M-$10M, $10M-$20M, $20M+
var DONUT_COLORS = ["#B25573", "#79B0CD", "#CA6700", "#01657F"];

// ── Styles (2x scaled for 1200px render width) ──
var S = {
  root: { fontFamily: FONT_HEADING, backgroundColor: C.bg, color: C.text, padding: 0, width: "100%" },
  section: { padding: "70px 40px", borderBottom: "2px solid " + C.border },
  sectionTitle: { fontSize: 58, fontWeight: 400, color: C.white, textAlign: "center", textTransform: "uppercase", letterSpacing: 3, marginBottom: 12, fontFamily: FONT_HEADING },
  sectionDate: { textAlign: "center", fontSize: 32, color: C.muted, marginBottom: 6, fontFamily: FONT_BODY },
  sectionSubtitle: { textAlign: "center", fontSize: 28, color: C.dim, marginBottom: 40, fontFamily: FONT_BODY },
  bigNumber: { fontSize: 160, fontWeight: 800, color: C.white, textAlign: "center", lineHeight: 1, fontFamily: FONT_BODY, fontFeatureSettings: FONT_FEATURE_LNUM },
  bigNumberLabel: { textAlign: "center", fontSize: 28, color: C.muted, textTransform: "uppercase", letterSpacing: 2, marginBottom: 12, fontFamily: FONT_BODY },
  changeBadges: { display: "flex", justifyContent: "center", gap: 12, marginTop: 16, flexWrap: "wrap" },
  badgeUp: { fontSize: 24, padding: "8px 20px", borderRadius: 6, fontWeight: 600, background: "rgba(0,255,0,0.15)", color: COLOR_POSITIVE, fontFamily: FONT_BODY },
  badgeDown: { fontSize: 24, padding: "8px 20px", borderRadius: 6, fontWeight: 600, background: "rgba(255,0,0,0.15)", color: COLOR_NEGATIVE, fontFamily: FONT_BODY },
  chartLegend: { display: "flex", justifyContent: "center", gap: 28, margin: "28px 0 16px" },
  legendItem: { display: "flex", alignItems: "center", gap: 10, fontSize: 24, color: C.muted, fontWeight: 600, fontFamily: FONT_BODY },
  legendDot: function(color) { return { width: 16, height: 16, borderRadius: "50%", backgroundColor: color, display: "inline-block" }; },
  donutSection: { position: "relative", width: 1000, height: 1000, margin: "40px auto" },
  donutCenter: { position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", zIndex: 2 },
  donutAmount: { fontSize: 100, fontWeight: 800, color: "#CC8E1C", fontFamily: FONT_BODY, fontFeatureSettings: FONT_FEATURE_LNUM },
  donutLabel: { fontSize: 40, fontWeight: 700, color: C.white, textTransform: "uppercase", letterSpacing: 3, marginTop: 6, fontFamily: FONT_HEADING },
  donutLabelSub: { fontSize: 40, fontWeight: 600, color: C.white, textTransform: "uppercase", letterSpacing: 2, fontFamily: FONT_HEADING },
  table: { width: "100%", borderCollapse: "collapse", marginTop: 30, fontSize: 32 },
  th: { textAlign: "left", fontSize: 20, fontWeight: 600, color: C.dim, textTransform: "uppercase", padding: "16px 14px", borderBottom: "2px solid rgba(255,255,255,0.5)", whiteSpace: "nowrap" },
  thRight: { textAlign: "right", fontSize: 20, fontWeight: 600, color: C.dim, textTransform: "uppercase", padding: "16px 14px", borderBottom: "2px solid rgba(255,255,255,0.5)", whiteSpace: "nowrap" },
  td: { padding: "18px 20px", fontSize: 32, color: C.text, borderBottom: "1px solid rgba(255,255,255,0.5)", fontFamily: FONT_BODY },
  tdRight: { padding: "18px 20px", fontSize: 32, color: C.text, borderBottom: "1px solid rgba(255,255,255,0.5)", textAlign: "right", fontFamily: FONT_BODY },
  tdPositive: { padding: "18px 20px", fontSize: 32, borderBottom: "1px solid rgba(255,255,255,0.5)", textAlign: "right", fontFamily: FONT_BODY },
  tdNegative: { padding: "18px 20px", fontSize: 32, borderBottom: "1px solid rgba(255,255,255,0.5)", textAlign: "right", fontFamily: FONT_BODY },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 },
  statCard: { textAlign: "center", padding: "40px 0" },
  statCardTitle: { fontSize: 36, fontWeight: 800, color: C.white, textTransform: "uppercase", letterSpacing: 2, lineHeight: 1.3, marginBottom: 20, fontFamily: FONT_HEADING },
  statCardNumber: { fontSize: 100, fontWeight: 800, color: C.white, lineHeight: 1, fontFamily: FONT_HEADING, fontFeatureSettings: FONT_FEATURE_LNUM },
  statCardChange: function(positive) { return { fontSize: 26, marginTop: 12, fontWeight: 500 }; },
  sourceLabel: { textAlign: "center", fontSize: 22, color: C.dim, marginTop: 12, fontStyle: "italic", fontFamily: FONT_BODY },
  largeValue: { fontSize: 100, fontWeight: 800, color: C.white, textAlign: "center", lineHeight: 1, marginTop: 12, fontFamily: FONT_HEADING, fontFeatureSettings: FONT_FEATURE_LNUM },
};

var formatBigCurrency = function(val) {
  if (!val || val <= 0) return "--";
  if (val >= 1e9) return "$" + (val / 1e9).toFixed(2) + "B";
  if (val >= 1e6) return "$" + (val / 1e6).toFixed(2) + "M";
  if (val >= 1e3) return "$" + (val / 1e3).toFixed(0) + "K";
  return "$" + _formatNumber(val);
};

var pctChange = function(current, prior) {
  if (!prior || !current) return null;
  return ((current - prior) / prior * 100).toFixed(1);
};

// ── Comparison text helper — only the percentage gets color, "compared to..." gets COLOR_COMPARISON_TEXT ──
var ComparisonText = function(props) {
  var pct = props.pct;
  var text = props.text;
  var fontSize = props.fontSize || 28;
  var align = props.align || "inherit";
  if (pct == null) return null;
  var isPositive = Number(pct) >= 0;
  return (
    <div style={{ fontSize: fontSize, fontFamily: FONT_BODY, textAlign: align }}>
      <span style={{ color: isPositive ? COLOR_POSITIVE : COLOR_NEGATIVE, fontWeight: 600 }}>
        {isPositive ? "+" : ""}{pct}%
      </span>
      <span style={{ color: COLOR_COMPARISON_TEXT }}> {text}</span>
    </div>
  );
};

// ── Custom tick renderers to force white fill ──
var XTick = function(props) {
  var x = props.x;
  var y = props.y;
  var payload = props.payload;
  var anchor = props.textAnchor || "middle";
  var formatter = props.tickFormatter;
  var index = props.index;
  var val = formatter ? formatter(payload.value, index) : payload.value;
  return (
    <g transform={"translate(" + x + "," + y + ")"}>
      <text x={0} y={0} textAnchor={anchor} fill="#ffffff" style={{ fill: "#ffffff" }} fontSize={32} fontWeight={500} fontFamily={FONT_BODY} dy={50}>
        {val}
      </text>
    </g>
  );
};

var YTick = function(props) {
  var x = props.x;
  var y = props.y;
  var payload = props.payload;
  var anchor = props.textAnchor || "end";
  var formatter = props.tickFormatter;
  var index = props.index;
  var raw = formatter ? formatter(payload.value, index) : payload.value;
  var val = typeof raw === "number" ? raw.toLocaleString() : raw;
  return (
    <g transform={"translate(" + x + "," + y + ")"}>
      <text x={0} y={0} textAnchor={anchor} fill="#ffffff" style={{ fill: "#ffffff" }} fontSize={32} fontWeight={500} fontFamily={FONT_BODY} dy={4}>
        {val}
      </text>
    </g>
  );
};

// ── Weekly x-axis tick factory (52-week hatch marks above, monthly hatch marks + labels below) ──
// Month boundaries and labels are evenly distributed across the 52-week axis
// (12 months ÷ 52 weeks ≈ 4.33 weeks per month) rather than snapped to the
// calendar week that happens to contain the 1st or 15th — this gives uniform
// visual spacing along the bottom of every weekly chart.
var computeWeekMeta = function(year) {
  var monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  var meta = [];
  for (var w = 0; w < 52; w++) {
    meta.push({ monthBoundary: false, monthLabel: null });
  }

  var weeksPerMonth = 52 / 12;
  for (var m = 0; m < 12; m++) {
    var boundaryIdx = Math.round(m * weeksPerMonth);
    if (boundaryIdx >= 0 && boundaryIdx < 52) meta[boundaryIdx].monthBoundary = true;

    var labelIdx = Math.round(m * weeksPerMonth + weeksPerMonth / 2);
    if (labelIdx >= 0 && labelIdx < 52) meta[labelIdx].monthLabel = monthNames[m];
  }

  return meta;
};

var createWeeklyTick = function(weekMeta) {
  return function(props) {
    var x = props.x, y = props.y, index = props.index;
    var meta = weekMeta[index] || {};
    return (
      <g transform={"translate(" + x + "," + y + ")"}>
        {meta.monthBoundary && <line x1={0} y1={0} x2={0} y2={12} stroke="#ffffff" strokeWidth={1.5} />}
        {meta.monthLabel && (
          <text x={0} y={32} textAnchor="middle" fill="#ffffff" fontSize={24} fontFamily={FONT_BODY}>
            {meta.monthLabel}
          </text>
        )}
      </g>
    );
  };
};

// ── Ten-year (S&P) x-axis tick factory: quarterly hatch above, yearly hatch + label below ──
var createTenYearTick = function(chartData) {
  var meta = chartData.map(function(d) {
    var parts = d.x.split(" ");
    return { month: parts[0], year: parseInt(parts[1]) };
  });
  return function(props) {
    var x = props.x, y = props.y, index = props.index;
    var m = meta[index];
    if (!m) return null;
    return (
      <g transform={"translate(" + x + "," + y + ")"}>
        {["Jan","Apr","Jul","Oct"].indexOf(m.month) >= 0 && (
          <line x1={0} y1={-8} x2={0} y2={0} stroke="#ffffff" strokeWidth={1} />
        )}
        {m.month === "Jan" && (
          <line x1={0} y1={0} x2={0} y2={12} stroke="#ffffff" strokeWidth={1.5} />
        )}
        {m.month === "Jul" && (
          <text x={0} y={32} textAnchor="middle" fill="#ffffff" fontSize={22} fontFamily={FONT_BODY}>
            {m.year}
          </text>
        )}
      </g>
    );
  };
};

// ── Rates (long-term) x-axis tick factory: yearly hatch above, decade hatch + label below ──
var createRatesTick = function() {
  return function(props) {
    var x = props.x, y = props.y, payload = props.payload;
    var yr = parseInt(payload.value);
    if (isNaN(yr)) return null;
    return (
      <g transform={"translate(" + x + "," + y + ")"}>
        {yr % 10 === 0 && <line x1={0} y1={0} x2={0} y2={12} stroke="#ffffff" strokeWidth={1.5} />}
        {yr % 10 === 0 && (
          <text x={6} y={32} textAnchor="start" fill="#ffffff" fontSize={22} fontFamily={FONT_BODY}>
            {yr + "s"}
          </text>
        )}
      </g>
    );
  };
};

// ── Chart: multi-year line (with weekly x-axis support) ──
var MultiYearLineChart = function(props) {
  var datasets = props.datasets; // [{ label, color, data: [{x, y}], bold }]
  var height = props.height || 580;
  var yMin = props.yMin;
  var yMax = props.yMax;
  var yTicks = props.yTicks;
  var weekMeta = props.weekMeta;
  var connectNulls = props.connectNulls || false;
  if (!datasets || datasets.length === 0) return null;

  var labels = [];
  datasets.forEach(function(ds) {
    ds.data.forEach(function(d) {
      if (labels.indexOf(d.x) === -1) labels.push(d.x);
    });
  });
  labels.sort(function(a, b) { return a - b; });

  var chartData = labels.map(function(label) {
    var point = { name: label };
    datasets.forEach(function(ds) {
      var match = ds.data.filter(function(d) { return d.x === label; })[0];
      point[ds.label] = match ? match.y : null;
    });
    return point;
  });

  var domainY = ["auto", "auto"];
  if (yMin != null && yMax != null) domainY = [yMin, yMax];
  else if (yMin != null) domainY = [yMin, "auto"];

  // Plot-area margins used both by Recharts and the month-label overlay so
  // boundary ticks line up with the chart's axis line.
  var Y_AXIS_WIDTH = 100;
  var RIGHT_MARGIN = 10;
  var LABEL_ROW_HEIGHT = weekMeta ? 60 : 0;
  var monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  var chart = (
    <ResponsiveContainer width="100%" height={weekMeta ? height - LABEL_ROW_HEIGHT : height}>
      <LineChart data={chartData} margin={{ top: 30, right: RIGHT_MARGIN, left: 0, bottom: weekMeta ? 8 : 60 }}>
        <XAxis
          dataKey="name"
          tick={weekMeta ? false : XTick}
          axisLine={{ stroke: "#ffffff", strokeWidth: 1 }}
          tickLine={false}
          interval={0}
        />
        <YAxis tick={YTick} axisLine={false} tickLine={false} domain={domainY} ticks={yTicks} width={Y_AXIS_WIDTH} />
        <CartesianGrid horizontal={true} vertical={false} stroke={C.gridLine} />
        <defs>
          <filter id="current-year-line-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#000000" floodOpacity="0.5" />
          </filter>
        </defs>
        {datasets.slice().reverse().map(function(ds) {
          // Current year: straight line segments; past years: smoothed
          var lineType = ds.bold ? "linear" : "monotone";
          return (
            <Line
              key={ds.label}
              type={lineType}
              dataKey={ds.label}
              stroke={ds.color}
              strokeWidth={ds.bold ? 10 : 5}
              style={Object.assign(
                { strokeWidth: ds.bold ? 10 : 5 },
                ds.bold ? { filter: "url(#current-year-line-shadow)" } : {}
              )}
              dot={false}
              connectNulls={connectNulls}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );

  if (!weekMeta) return chart;

  // Month labels rendered as a flex row independent of week buckets so
  // each month occupies an equal fraction of the chart width.
  return (
    <div style={{ position: "relative", height: height, width: "100%" }}>
      {chart}
      <div style={{
        position: "absolute",
        left: Y_AXIS_WIDTH,
        right: RIGHT_MARGIN,
        bottom: 0,
        height: LABEL_ROW_HEIGHT,
        display: "flex",
        pointerEvents: "none",
      }}>
        {monthNames.map(function(name, i) {
          return (
            <div key={name} style={{ flex: 1, position: "relative", textAlign: "center" }}>
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 1.5,
                height: 12,
                background: "#ffffff",
              }} />
              <span style={{
                position: "absolute",
                top: 20,
                left: 0,
                right: 0,
                textAlign: "center",
                color: "#ffffff",
                fontSize: 32,
                fontWeight: 500,
                fontFamily: FONT_BODY,
              }}>{name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Chart: filled area (for S&P) ──
var FilledAreaChart = function(props) {
  var data = props.data;
  var height = props.height || 700;
  var color = props.color || C.red;
  var yFormatter = props.yFormatter;
  var xTick = props.xTick;
  var yMin = props.yMin;
  if (!data || data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 30, right: 10, left: 10, bottom: 80 }}>
        <defs>
          <linearGradient id={"fill_" + color.replace("#", "")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="x"
          tick={xTick || XTick}
          axisLine={{ stroke: "#ffffff", strokeWidth: 1 }}
          tickLine={false}
          interval={0}
        />
        <YAxis
          tick={YTick}
          axisLine={false}
          tickLine={false}
          domain={yMin != null ? [yMin, "auto"] : ["auto", "auto"]}
          tickFormatter={yFormatter || function(v) { return v; }}
          width={110}
        />
        <CartesianGrid horizontal={true} vertical={false} stroke={C.gridLine} />
        <Area type="linear" dataKey="y" stroke={color} strokeWidth={4} fill={"url(#fill_" + color.replace("#", "") + ")"} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ── Small filled area chart for stat cards ──
var SmallAreaChart = function(props) {
  var data = props.data;
  var height = props.height || 480;
  var color = props.color || C.blue;
  var xFormatter = props.xFormatter;
  if (!data || data.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 30, right: 10, left: 0, bottom: 60 }}>
        <defs>
          <linearGradient id={"sfill_" + color.replace("#", "")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={color} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <XAxis dataKey="x" tick={XTick} axisLine={false} tickLine={{ stroke: "#ffffff", strokeWidth: 1 }} tickSize={10} interval="preserveStartEnd" tickFormatter={xFormatter} />
        <YAxis
          tick={YTick}
          axisLine={false}
          tickLine={false}
          domain={[0, "auto"]}
          tickFormatter={function(v) { return v + "%"; }}
          width={70}
        />
        <Area type="linear" dataKey="y" stroke={color} strokeWidth={3} fill={"url(#sfill_" + color.replace("#", "") + ")"} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ── Donut chart (with gradient + shadow for dimensionality) ──
var DonutChart = function(props) {
  var data = props.data; // [{ name, value }]
  if (!data || data.length === 0) return null;

  return (
    <PieChart width={1000} height={1000}>
      <defs>
        {DONUT_COLORS.map(function(color, i) {
          return (
            <linearGradient key={"dg" + i} id={"donutGrad_" + i} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={1} />
              <stop offset="100%" stopColor={color} stopOpacity={0.65} />
            </linearGradient>
          );
        })}
        <filter id="donutShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="8" result="blur" />
          <feOffset in="blur" dx="3" dy="5" result="offsetBlur" />
          <feFlood floodColor="#000000" floodOpacity="0.35" result="color" />
          <feComposite in="color" in2="offsetBlur" operator="in" result="shadow" />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <Pie
        data={data}
        cx={500} cy={500}
        innerRadius={340} outerRadius={480}
        dataKey="value"
        strokeWidth={0}
        style={{ filter: "url(#donutShadow)" }}
      >
        {data.map(function(entry, idx) {
          return <Cell key={idx} fill={"url(#donutGrad_" + (idx % DONUT_COLORS.length) + ")"} />;
        })}
      </Pie>
    </PieChart>
  );
};

// ── Legend row ──
var ChartLegend = function(props) {
  var items = props.items; // [{ label, color }]
  return (
    <div style={S.chartLegend}>
      {items.map(function(item, i) {
        return (
          <span key={i} style={S.legendItem}>
            <span style={S.legendDot(item.color)} />
            {" " + item.label}
          </span>
        );
      })}
    </div>
  );
};

// FRED data helpers
var _parseFredDate = function(dateStr) {
  // Parse as UTC to avoid timezone shifting "2016-01-01" to Dec 2015
  var parts = dateStr.split("-");
  return new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
};

var formatFredToChart = function(observations, frequency) {
  if (!observations || observations.length === 0) return [];
  return observations.map(function(obs) {
    var d = _parseFredDate(obs.date);
    var label;
    if (frequency === "a") {
      label = "" + d.getUTCFullYear();
    } else if (frequency === "m") {
      var months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      label = months[d.getUTCMonth()] + " " + d.getUTCFullYear();
    } else {
      label = (d.getUTCMonth() + 1) + "/" + d.getUTCDate() + "/" + (d.getUTCFullYear() % 100);
    }
    return { x: label, y: obs.value };
  });
};

// Merge two FRED observation arrays into shared chart data with two series
var mergeFredSeries = function(obsA, obsB, keyA, keyB, frequency) {
  var map = {};
  var order = [];
  var months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  function addObs(obs, key) {
    if (!obs) return;
    obs.forEach(function(o) {
      var d = _parseFredDate(o.date);
      var label = frequency === "a" ? "" + d.getUTCFullYear() : months[d.getUTCMonth()] + " " + d.getUTCFullYear();
      if (!map[label]) { map[label] = { x: label }; order.push(label); }
      map[label][key] = o.value;
    });
  }
  addObs(obsA, keyA);
  addObs(obsB, keyB);
  return order.map(function(label) { return map[label]; });
};

var getLatestValue = function(observations) {
  if (!observations || observations.length === 0) return null;
  return observations[observations.length - 1].value;
};

var getSecondLatestValue = function(observations) {
  if (!observations || observations.length < 2) return null;
  return observations[observations.length - 2].value;
};

// Donut and volume table data are built from real facet data in the component

// Year legends are built dynamically from real data in the component

// ── Main Layout ──
var Layout = function(props) {
  var fetchInProgress = props.fetchInProgress;
  var setFetchInProgress = props.setFetchInProgress;
  var user = props.user;
  var setReportLoaded = props.setReportLoaded;
  var reportData = props.reportData;
  var setReportData = props.setReportData;
  var searchTags = props.searchTags;
  var summarizeGeographies = props.summarizeGeographies;
  var customDate = props.customDate;
  var submit = props.submit;
  var setSubmit = props.setSubmit;

  // Default to last week (Mon-Sun)
  var lastWeekStart = moment().subtract(1, "week").startOf("isoWeek");
  var lastWeekEnd = moment().subtract(1, "week").endOf("isoWeek");
  // Prior week = two weeks ago (Mon-Sun)
  var priorWeekStart = moment().subtract(2, "weeks").startOf("isoWeek");
  var priorWeekEnd = moment().subtract(2, "weeks").endOf("isoWeek");
  // Same week last year
  var lastYearWeekStart = lastWeekStart.clone().subtract(1, "year");
  var lastYearWeekEnd = lastWeekEnd.clone().subtract(1, "year");
  var priorYearPeriodStr = "[" + lastYearWeekStart.utc().format() + " TO " + lastYearWeekEnd.utc().format() + "]";
  // Build Solr time ranges directly — getPriorPeriod() returned "" because previousStartDate/previousEndDate were never set
  var currentPeriod = "[" + lastWeekStart.clone().utc().format() + " TO " + lastWeekEnd.clone().utc().format() + "]";
  var priorPeriod = "[" + priorWeekStart.clone().utc().format() + " TO " + priorWeekEnd.clone().utc().format() + "]";
  var dateOptions = Object.assign({}, customDate.dateOptions, {
    startDate: lastWeekStart,
    endDate: lastWeekEnd,
    gap: "+7DAY",
  });
  var rangeEnd = getRangeEnd(dateOptions);
  var rangeStart = getRangeStart;

  // Date formatting: drop leading zeros, handle cross-month ranges
  var startStr = lastWeekStart.format("MMMM D");
  var endStr = lastWeekStart.month() === lastWeekEnd.month()
    ? lastWeekEnd.format("D, YYYY")
    : lastWeekEnd.format("MMMM D, YYYY");
  var currentPeriodName = "Week of " + startStr + " - " + endStr;

  var _inv = useState(null);
  var inventoryCount = _inv[0];
  var setInventoryCount = _inv[1];

  useEffect(function() {
    if (!reportData && !fetchInProgress && submit) {
      loadData();
      setSubmit(false);
    }
  }, [submit]);

  var statFacet = {
    avg_price: "avg(price)",
    med_price: "percentile(price, 50)",
    sum_price: "sum(price)",
    ppsf: { type: "query", q: "sq_ft:[300 TO 20000]", facet: { avg: "avg(div(price, sq_ft))", med: "percentile(div(price, sq_ft), 50)" } },
    dom: { type: "query", q: "days_on_market:[3 TO 720]", facet: { avg: "avg(days_on_market)", med: "percentile(days_on_market, 50)" } },
  };

  var MANHATTAN_GEO_Q = '((geocode:"Intersects(POLYGON((-73.97778826567416 40.79717263461296, -73.94276191908436 40.78255596254556, -73.9408313330513 40.77430656329429, -73.96854903251845 40.741612165071814, -73.96874059644324 40.72220180307545, -73.97525376990846 40.70942449288347, -73.9790850484172 40.70739151289348, -73.99804987703672 40.7050680311948, -74.01241717144515 40.695773293791916, -74.0264013380032 40.70782715668773, -74.01682314173051 40.7391860200006, -74.0069612479541 40.76647334481791, -73.9960421042035 40.78069026781637, -73.97778826567416 40.79717263461296)))"))';

  var loadData = function() {
    var q = MANHATTAN_GEO_Q;
    var locQuery = _tranformTagsQuery(searchTags);
    if (locQuery) q = locQuery;

    var defaultQ = getCohortQuery(user, true);
    var fq = [].concat(_activityFQ.slice(), ["{!join from=alternate_keys fromIndex=we3-buildings to=building}" + defaultQ, "price:[3000000 TO *]"]);
    setFetchInProgress(true);

    // Annual WEEKLY range facets for multi-year charts (changed from monthly to weekly)
    var currentYear = lastWeekEnd.year();
    var contractYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
    var listingYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

    // Align weekly buckets to ISO weeks (Mon–Sun) so the chart's last completed
    // week matches the top stat's "last full ISO week" window.
    var isoWeekStart = function(yr) {
      return moment({ year: yr, month: 0, day: 1 }).startOf("isoWeek").utc().format();
    };
    var annualContractFacets = {};
    contractYears.forEach(function(yr) {
      annualContractFacets["y" + yr] = {
        type: "query",
        q: "topic:listings AND event_tags:contract-signed AND -event_tags:rent AND -event_tags:*commercial*",
        facet: {
          weeks: {
            range: { field: "time", start: isoWeekStart(yr), end: isoWeekStart(yr + 1), gap: "+7DAY" }
          }
        }
      };
    });

    var annualListingFacets = {};
    listingYears.forEach(function(yr) {
      annualListingFacets["y" + yr] = {
        type: "query",
        q: "topic:listings AND event_tags:just-listed AND -event_tags:rent AND -event_tags:*commercial*",
        facet: {
          weeks: {
            range: { field: "time", start: isoWeekStart(yr), end: isoWeekStart(yr + 1), gap: "+7DAY" }
          }
        }
      };
    });

    var priceRangeFacet = {
      "r_3m_5m": { type: "query", q: "price:[3000000 TO 4999999]", facet: { vol: "sum(price)" } },
      "r_5m_10m": { type: "query", q: "price:[5000000 TO 9999999]", facet: { vol: "sum(price)" } },
      "r_10m_20m": { type: "query", q: "price:[10000000 TO 19999999]", facet: { vol: "sum(price)" } },
      "r_20m_plus": { type: "query", q: "price:[20000000 TO *]", facet: { vol: "sum(price)" } },
    };

    var currentPeriodFacet = Object.assign({}, statFacet, priceRangeFacet);
    var priorPeriodFacet = Object.assign({}, statFacet, priceRangeFacet);

    var dataFacet = {
      contract_signed: {
        type: "query",
        q: "topic:listings AND event_tags:contract-signed AND -event_tags:rent AND -event_tags:*commercial*",
        facet: {
          current_period: { type: "query", q: "time:" + currentPeriod, facet: currentPeriodFacet },
          prior_period: { type: "query", q: "time:" + priorPeriod, facet: priorPeriodFacet },
          prior_year_period: { type: "query", q: "time:" + priorYearPeriodStr, facet: statFacet },
          chart: { range: { field: "time", start: rangeStart, end: rangeEnd, gap: "+7DAY", facet: { sum_price: "sum(price)" } } },
        },
      },
      new_listings: {
        type: "query",
        q: "topic:listings AND event_tags:just-listed AND -event_tags:rent AND -event_tags:*commercial*",
        facet: { current_period: { type: "query", q: "time:" + currentPeriod, facet: statFacet } },
      },
      annual_contracts: { type: "query", q: "*:*", facet: annualContractFacets },
      annual_listings: { type: "query", q: "*:*", facet: annualListingFacets },
    };

    var facet = { all: { type: "query", q: "event_tags:(condo* co?op* coop* condo*p single?family)", facet: dataFacet } };

    // Inventory — active listings from search-listings index (unique properties)
    var invQ = _tranformTagsQuery(searchTags) || MANHATTAN_GEO_Q;
    var periodEnd = lastWeekEnd.clone().utc().format();
    var invPromise = _axiosCall({
      url: apiURL + "/query/search-listings",
      method: "post",
      query: {
        q: invQ,
        fq: [
          "source_namespace:(REBNY-IDX)",
          "deleted:false",
          "price:[3000000 TO *]",
          "listing_type:sale",
          "active_date_start:[* TO " + periodEnd + "]",
          "active_date_end:[" + periodEnd + " TO *]"
        ],
        rows: 0, wt: "json",
        "json.facet": { unique_props: "unique(property)" },
      },
    }).catch(function(err) { console.log("inventory error: " + err); });

    // Wait for both calls, then set data together
    var mainPromise = _axiosCall({
      url: apiURL + "/query/we3-activities-3?cache_time=1440&always-cache=true",
      method: "post",
      query: { q: q, fq: fq, wt: "json", rows: 0, "json.facet": facet },
    }).catch(function(err) { console.log("error: " + err); });

    // Inventory annual chart — weekly snapshots using facet queries with unique(property).
    // Split per year (one request per year) so each Solr request stays small
    // enough to return; ~50 unique() facets per request rather than ~200 combined.
    var invChartYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
    var nowMoment = moment();
    var invChartPromisesByYear = invChartYears.map(function(yr) {
      var perYearFacet = {};
      // Anchor on the first ISO week of the year. If that week's Monday falls
      // in the prior year (e.g. 2023 starts on a Sunday so its first ISO week
      // is Dec 26 2022 – Jan 1 2023), shift forward one week so the chart
      // doesn't pull a snapshot from the prior year.
      var weekCursor = moment({ year: yr, month: 0, day: 1 }).startOf("isoWeek");
      if (weekCursor.year() < yr) {
        weekCursor.add(1, "weeks");
      }
      for (var wi = 0; wi < 52; wi++) {
        var snapDate = weekCursor.clone().add(wi, "weeks");
        if (snapDate.isAfter(nowMoment)) continue;
        var snapStart = snapDate.clone().startOf("isoWeek").utc().format();
        var snapEnd = snapDate.clone().endOf("isoWeek").utc().format();
        var sourceNs = yr < 2026 ? "REBNY-VOW" : "REBNY-IDX";
        perYearFacet["inv_" + yr + "_" + wi] = {
          type: "query",
          q: "source_namespace:(" + sourceNs + ") AND active_date_start:[* TO " + snapEnd + "] AND active_date_end:[" + snapStart + " TO *]",
          facet: { unique_props: "unique(property)" }
        };
      }
      return _axiosCall({
        url: apiURL + "/query/search-listings?cache_time=1440&always-cache=true",
        method: "post",
        query: {
          q: invQ,
          fq: ["price:[3000000 TO *]", "listing_type:sale"],
          rows: 0, wt: "json",
          "json.facet": perYearFacet,
        },
      }).catch(function(err) { console.log("inventory chart error (year " + yr + "): " + err); return null; });
    });
    var invChartPromise = Promise.all(invChartPromisesByYear).then(function(perYearResults) {
      // Merge facets from all per-year responses into a single shape so the
      // downstream extraction code below remains a no-op change.
      var merged = { facets: {} };
      perYearResults.forEach(function(r) {
        if (r && r.facets) {
          Object.keys(r.facets).forEach(function(k) { merged.facets[k] = r.facets[k]; });
        }
      });
      return merged;
    });

    // FRED data — S&P 500 (monthly, 10 years starting 2017), mortgage & treasury (annual, 7 years), all current rates
    var fredSP500 = _axiosCall({ url: apiURL + "/fred/sp500?start=2017-01-01&frequency=m", method: "get" }).catch(function(e) { console.log("FRED SP500 error:", e); return null; });
    var fredSP500Weekly = _axiosCall({ url: apiURL + "/fred/sp500?frequency=w", method: "get" }).catch(function(e) { return null; });
    var fredMortgage30 = _axiosCall({ url: apiURL + "/fred/mortgage30?start=1980-01-01&frequency=a", method: "get" }).catch(function(e) { return null; });
    var fredMortgage30Weekly = _axiosCall({ url: apiURL + "/fred/mortgage30?frequency=w", method: "get" }).catch(function(e) { return null; });
    var fredTreasury10 = _axiosCall({ url: apiURL + "/fred/treasury10?start=1980-01-01&frequency=a", method: "get" }).catch(function(e) { return null; });
    var fredTreasury10Weekly = _axiosCall({ url: apiURL + "/fred/treasury10?frequency=w", method: "get" }).catch(function(e) { return null; });
    var fredRates = _axiosCall({ url: apiURL + "/fred?series=mortgage30,mortgage15,treasury10,fedfunds,unemployment,cpi&frequency=m", method: "get" }).catch(function(e) { return null; });

    Promise.all([mainPromise, invPromise, invChartPromise, fredSP500, fredSP500Weekly, fredMortgage30, fredMortgage30Weekly, fredTreasury10, fredTreasury10Weekly, fredRates]).then(function(results) {
      var res = results[0];
      var invRes = results[1];
      var invChartRes = results[2];
      var fredSP = results[3];
      var fredSPWeekly = results[4];
      var fredMort = results[5];
      var fredMortWeekly = results[6];
      var fredTreas = results[7];
      var fredTreasWeekly = results[8];
      var fredAllRates = results[9];
      if (res && res.facets) {
        // Extract inventory count (unique properties)
        var invCount = null;
        if (invRes && invRes.facets && invRes.facets.unique_props != null) invCount = invRes.facets.unique_props;
        else if (invRes && invRes.numFound != null) invCount = invRes.numFound;

        res._inventoryCount = invCount;

        // Extract inventory chart data from facet query results
        var invChartMap = {};
        var invFacets = invChartRes && invChartRes.facets ? invChartRes.facets : null;
        if (invFacets) {
          Object.keys(invFacets).forEach(function(facetKey) {
            if (facetKey.indexOf("inv_") === 0 && invFacets[facetKey]) {
              invChartMap[facetKey] = invFacets[facetKey].unique_props || 0;
            }
          });
        }
        res._inventoryChart = invChartMap;

        // Store FRED data
        res._fredSP500 = fredSP && fredSP.observations ? fredSP.observations : null;
        res._fredSP500Weekly = fredSPWeekly && fredSPWeekly.observations ? fredSPWeekly.observations : null;
        res._fredMortgage30 = fredMort && fredMort.observations ? fredMort.observations : null;
        res._fredMortgage30Weekly = fredMortWeekly && fredMortWeekly.observations ? fredMortWeekly.observations : null;
        res._fredTreasury10 = fredTreas && fredTreas.observations ? fredTreas.observations : null;
        res._fredTreasury10Weekly = fredTreasWeekly && fredTreasWeekly.observations ? fredTreasWeekly.observations : null;
        res._fredRates = fredAllRates || null;

        setFetchInProgress(false);
        setReportLoaded(true);
        setSubmit(false);
        setReportData(res);
      }
    });
  };

  var vnd = function(obj, path) {
    var keys = path.split(".");
    var cur = obj;
    for (var i = 0; i < keys.length; i++) {
      if (cur == null || cur[keys[i]] == null) return null;
      cur = cur[keys[i]];
    }
    return cur;
  };

  var contractCount = vnd(reportData, "facets.all.contract_signed.current_period.count");
  var contractCountPrior = vnd(reportData, "facets.all.contract_signed.prior_period.count");
  var contractCountPriorYear = vnd(reportData, "facets.all.contract_signed.prior_year_period.count");
  var totalVolume = vnd(reportData, "facets.all.contract_signed.current_period.sum_price");

  // Price range breakdown for donut + table (updated breakpoints: $10M-$20M, $20M+)
  var priceRangeKeys = [
    { key: "r_3m_5m", label: "$3M-$5M" },
    { key: "r_5m_10m", label: "$5M-$10M" },
    { key: "r_10m_20m", label: "$10M-$20M" },
    { key: "r_20m_plus", label: "$20M+" },
  ];

  var volumeTableData = priceRangeKeys.map(function(pr) {
    var curVol = vnd(reportData, "facets.all.contract_signed.current_period." + pr.key + ".vol") || 0;
    var priorVol = vnd(reportData, "facets.all.contract_signed.prior_period." + pr.key + ".vol") || 0;
    var pctOfTotal = totalVolume && totalVolume > 0 ? ((curVol / totalVolume) * 100).toFixed(1) : "0.0";
    var wowChange = (priorVol > 0 && curVol > 0) ? (((curVol - priorVol) / priorVol) * 100).toFixed(1) : null;
    return {
      range: pr.label,
      vol: formatBigCurrency(curVol),
      pct: pctOfTotal + "%",
      change: wowChange != null ? (Number(wowChange) >= 0 ? "+" : "") + wowChange + "%" : "--",
      positive: wowChange != null && Number(wowChange) >= 0,
    };
  });

  var donutData = priceRangeKeys.map(function(pr) {
    var curVol = vnd(reportData, "facets.all.contract_signed.current_period." + pr.key + ".vol") || 0;
    return { name: pr.label, value: curVol };
  });
  var newListingsCount = vnd(reportData, "facets.all.new_listings.current_period.count");

  var weekChange = pctChange(contractCount, contractCountPrior);
  var yearChange = pctChange(contractCount, contractCountPriorYear);
  var priorYearLabel = lastYearWeekStart.format("YYYY");

  // Build annual chart datasets from WEEKLY facet data
  var chartColors = ["#2FFA0B", "#FFFD10", "#FF01FF", "#18F5FF"];
  var currentYear = lastWeekEnd.year();
  var contractChartYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
  var listingChartYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

  // Anchor week-index math to the first ISO week of the year (matches Solr range).
  // currentWeekIndex points to the in-progress week — exclude it so the chart's
  // last visible bucket is the last fully completed ISO week.
  var isoYearStart = moment({ year: currentYear, month: 0, day: 1 }).startOf("isoWeek");
  var currentWeekIndex = Math.floor(moment().diff(isoYearStart, "days") / 7);

  var buildAnnualDatasets = function(facetPath, years) {
    return years.map(function(yr, idx) {
      var isCurrentYear = idx === 0;
      var buckets = vnd(reportData, facetPath + ".y" + yr + ".weeks.buckets") || [];
      var numWeeks = Math.min(buckets.length, 52);
      var data = [];
      for (var wi = 0; wi < numWeeks; wi++) {
        var b = buckets[wi];
        // For current year, null out the in-progress week and any future weeks
        if (isCurrentYear && wi >= currentWeekIndex) {
          data.push({ x: wi, y: null });
          continue;
        }
        data.push({ x: wi, y: b.count || 0 });
      }
      while (data.length < 52) {
        data.push({ x: data.length, y: null });
      }
      return { label: "" + yr, color: chartColors[idx % chartColors.length], data: data, bold: isCurrentYear };
    });
  };

  var contractDatasets = buildAnnualDatasets("facets.all.annual_contracts", contractChartYears);
  var listingDatasets = buildAnnualDatasets("facets.all.annual_listings", listingChartYears);

  // Build inventory chart datasets from weekly point-in-time facets (one point per week, 52-week axis)
  var invChartYears = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

  var inventoryDatasets = invChartYears.map(function(yr, idx) {
    var isCurrentYear = idx === 0;
    var data = [];
    var invChart = reportData && reportData._inventoryChart;
    for (var w = 0; w < 52; w++) {
      var facetKey = "inv_" + yr + "_" + w;
      var count = invChart && invChart[facetKey] != null ? invChart[facetKey] : null;
      data.push({ x: w, y: count });
    }
    return { label: "" + yr, color: chartColors[idx % chartColors.length], data: data, bold: isCurrentYear };
  });

  // Compute a uniform y-axis for inventory: pick a "nice" step so all 5 grid
  // lines are evenly spaced and round (no missing top line / variable gaps).
  var allInvValues = [];
  inventoryDatasets.forEach(function(ds) {
    ds.data.forEach(function(d) { if (d.y != null) allInvValues.push(d.y); });
  });
  var invDataMin = allInvValues.length > 0 ? Math.min.apply(null, allInvValues) : 0;
  var invDataMax = allInvValues.length > 0 ? Math.max.apply(null, allInvValues) : 0;
  var pickNiceStep = function(rawStep) {
    if (rawStep <= 0) return 1;
    var magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    var normalized = rawStep / magnitude;
    var niceMultiplier;
    if (normalized <= 1) niceMultiplier = 1;
    else if (normalized <= 2) niceMultiplier = 2;
    else if (normalized <= 2.5) niceMultiplier = 2.5;
    else if (normalized <= 5) niceMultiplier = 5;
    else niceMultiplier = 10;
    return niceMultiplier * magnitude;
  };
  var invTickCount = 5;
  var invPaddedMin = Math.max(0, invDataMin - (invDataMax - invDataMin) * 0.1);
  var invPaddedMax = invDataMax + (invDataMax - invDataMin) * 0.1;
  var invStep = pickNiceStep((invPaddedMax - invPaddedMin) / (invTickCount - 1));
  var invYMin = Math.max(0, Math.floor(invPaddedMin / invStep) * invStep);
  var invYMax = invYMin + invStep * (invTickCount - 1);
  while (invYMax < invDataMax) { invYMax += invStep; }
  var invTicks = [];
  for (var ti = invYMin; ti <= invYMax + 0.0001; ti += invStep) invTicks.push(ti);

  var contractLegend = contractChartYears.slice().reverse().map(function(yr) { var i = contractChartYears.indexOf(yr); return { label: "" + yr, color: chartColors[i] }; });
  var listingLegend = listingChartYears.slice().reverse().map(function(yr) { var i = listingChartYears.indexOf(yr); return { label: "" + yr, color: chartColors[i] }; });
  var inventoryLegend = invChartYears.slice().reverse().map(function(yr) { var i = invChartYears.indexOf(yr); return { label: "" + yr, color: chartColors[i] }; });

  // Compute weekly x-axis metadata for the shared x-axis treatment
  var weekMeta = computeWeekMeta(currentYear);

  if (!reportData) return null;

  var hasCustomArea = searchTags && searchTags.length > 0;
  var areaLabel = hasCustomArea ? _capitalizeText(summarizeGeographies(searchTags)) + ", $3M+" : "Manhattan South of 96th Street, $3M+";

  return (
    <div style={S.root} className="page-section no-header newsletter-custom">
      <style dangerouslySetInnerHTML={{ __html: "\
        #style2 .newsletter-custom .recharts-cartesian-axis-tick text,\
        .newsletter-custom .recharts-cartesian-axis-tick text {\
          font-size: 32px !important;\
          font-weight: 500 !important;\
          fill: #ffffff !important;\
          font-family: 'Roboto', Arial, sans-serif !important;\
        }\
      " }} />

      {/* ═══ WEEKLY CONTRACT ACTIVITY ═══ */}
      <div style={S.section}>
        <div className="editable" style={{ fontSize: 58, fontWeight: 400, color: "#ffffff", textTransform: "uppercase", letterSpacing: 3, marginBottom: 30, textAlign: "center", fontFamily: FONT_HEADING, whiteSpace: "nowrap" }}>Weekly Contract Activity</div>
        <div className="editable" style={{ fontSize: 40, color: "#ffffff", marginBottom: 8, textAlign: "center", fontFamily: FONT_BODY }}>{currentPeriodName}</div>
        <div className="editable" style={{ fontSize: 36, color: "#ffffff", marginBottom: 50, textAlign: "center", fontFamily: FONT_BODY }}>{areaLabel}</div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 40, marginTop: 20 }}>
          <div style={{ fontSize: 180, fontWeight: 800, color: "#CC8E1C", lineHeight: 1.05, fontFamily: FONT_BODY, fontFeatureSettings: FONT_FEATURE_LNUM }}>{contractCount != null ? _formatNumber(contractCount) : "--"}</div>
          <div style={{ textAlign: "left", paddingTop: 20 }}>
            <div className="editable" style={{ fontSize: 34, fontWeight: 700, color: "#CC8E1C", textTransform: "uppercase", letterSpacing: 2, marginBottom: 12, fontFamily: FONT_BODY }}>New Signed Contracts</div>
            <ComparisonText pct={weekChange} text="compared to last week" />
            <ComparisonText pct={yearChange} text={"compared to same week in " + priorYearLabel} />
          </div>
        </div>

        <ChartLegend items={contractLegend} />
        <div style={{ height: 580, color: "#fff" }}>
          <MultiYearLineChart datasets={contractDatasets} height={580} yMin={0} weekMeta={weekMeta} />
        </div>
        <div className="editable" style={S.sourceLabel}>Source: Marketproof</div>
      </div>

      {/* ═══ TOTAL WEEKLY SALES VOLUME (Donut + Table) ═══ */}
      <div style={S.section}>
        <div style={S.donutSection}>
          <DonutChart data={donutData} />
          <div style={S.donutCenter}>
            <div style={S.donutAmount}>{totalVolume != null ? formatBigCurrency(totalVolume) : "--"}</div>
            <div className="editable" style={S.donutLabel}>Total Weekly</div>
            <div className="editable" style={S.donutLabelSub}>Sales Volume</div>
          </div>
        </div>
        <div className="editable" style={Object.assign({}, S.sectionDate, { marginBottom: 4 })}>{currentPeriodName}</div>
        <div className="editable" style={Object.assign({}, S.sectionSubtitle, { marginBottom: 20 })}>{areaLabel}</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}></th>
              <th className="editable" style={S.thRight}>Dollar Volume ($)</th>
              <th className="editable" style={S.thRight}>Dollar Volume (%)</th>
              <th className="editable" style={S.thRight}>Since Last Week</th>
            </tr>
          </thead>
          <tbody>
            {volumeTableData.map(function(row, i) {
              return (
                <tr key={i}>
                  <td style={Object.assign({}, S.td, { whiteSpace: "nowrap" })}><span style={{ display: "inline-block", width: 18, height: 18, borderRadius: "50%", backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length], marginRight: 12, verticalAlign: "middle" }} />{row.range}</td>
                  <td style={S.tdRight}>{row.vol}</td>
                  <td style={S.tdRight}>{row.pct}</td>
                  <td style={Object.assign({}, row.positive ? S.tdPositive : S.tdNegative, { color: row.change === "--" ? C.text : (row.positive ? COLOR_POSITIVE : COLOR_NEGATIVE) })}>{row.change}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="editable" style={S.sourceLabel}>Source: Marketproof</div>
      </div>

      {/* ═══ NEW LISTINGS ═══ */}
      <div style={S.section}>
        <div className="editable" style={S.sectionTitle}>New Listings</div>
        <div style={{ marginBottom: 20 }} />
        <div style={S.bigNumber}>{newListingsCount != null ? _formatNumber(newListingsCount) : "--"}</div>
        <div style={{ marginBottom: 16 }} />
        <div className="editable" style={Object.assign({}, S.sectionDate, { marginTop: 6 })}>{currentPeriodName}</div>
        <div className="editable" style={S.sectionSubtitle}>{areaLabel}</div>

        <ChartLegend items={listingLegend} />
        <div style={{ height: 580, color: "#fff" }}>
          <MultiYearLineChart datasets={listingDatasets} height={580} yMin={0} weekMeta={weekMeta} />
        </div>
        <div className="editable" style={S.sourceLabel}>Source: Marketproof</div>
      </div>

      {/* ═══ TOTAL LISTING INVENTORY ═══ */}
      <div style={S.section}>
        <div className="editable" style={S.sectionTitle}>Total Listing Inventory</div>
        <div style={{ marginBottom: 20 }} />
        <div style={S.bigNumber}>{reportData._inventoryCount != null ? _formatNumber(reportData._inventoryCount) : "--"}</div>
        <div style={{ marginBottom: 16 }} />
        <div className="editable" style={Object.assign({}, S.sectionDate, { marginTop: 6 })}>{currentPeriodName}</div>
        <div className="editable" style={S.sectionSubtitle}>{areaLabel}</div>

        <ChartLegend items={inventoryLegend} />
        <div style={{ height: 580, color: "#fff" }}>
          <MultiYearLineChart datasets={inventoryDatasets} height={580} yMin={invYMin} yMax={invYMax} yTicks={invTicks} weekMeta={weekMeta} connectNulls={true} />
        </div>
        <div className="editable" style={S.sourceLabel}>Source: Marketproof</div>
      </div>

      {/* ═══ 30-YR MORTGAGE + 10-YR TREASURY (Combined chart) ═══ */}
      {(function() {
        var mortObs = reportData._fredMortgage30;
        var treasObs = reportData._fredTreasury10;
        var mortWeeklyObs = reportData._fredMortgage30Weekly;
        var treasWeeklyObs = reportData._fredTreasury10Weekly;
        var chartData = mergeFredSeries(mortObs, treasObs, "mortgage", "treasury", "a");
        var mortLatest = mortWeeklyObs ? getLatestValue(mortWeeklyObs) : (mortObs ? getLatestValue(mortObs) : null);
        var mortPrev = mortWeeklyObs ? getSecondLatestValue(mortWeeklyObs) : (mortObs ? getSecondLatestValue(mortObs) : null);
        var mortChange = mortLatest && mortPrev ? pctChange(mortLatest, mortPrev) : null;
        var treasLatest = treasWeeklyObs ? getLatestValue(treasWeeklyObs) : (treasObs ? getLatestValue(treasObs) : null);
        var treasPrev = treasWeeklyObs ? getSecondLatestValue(treasWeeklyObs) : (treasObs ? getSecondLatestValue(treasObs) : null);
        var treasChange = treasLatest && treasPrev ? pctChange(treasLatest, treasPrev) : null;
        var ratesTick = createRatesTick();
        return (
          <div style={Object.assign({}, S.section, { borderBottom: "none", paddingBottom: 50 })}>
            <div style={{ display: "flex", justifyContent: "center", gap: 80, marginTop: 20, marginBottom: 30 }}>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div className="editable" style={{ fontSize: 52, color: "#E8248D", fontWeight: 800, fontFamily: FONT_HEADING, marginBottom: 16, textTransform: "uppercase", letterSpacing: 3, lineHeight: 1.3 }}><span style={{ fontFamily: FONT_BODY, fontWeight: 700 }}>30-</span>Year<br/>Fixed Rate<br/>Mortgage Avg</div>
                <div style={{ fontSize: 100, fontWeight: 800, color: "#ffffff", lineHeight: 1, fontFamily: FONT_BODY, fontFeatureSettings: FONT_FEATURE_LNUM }}>{mortLatest != null ? mortLatest.toFixed(2) + "%" : "--"}</div>
                <div style={{ marginBottom: 12 }} />
                <ComparisonText pct={mortChange} text="compared to last week" fontSize={26} />
              </div>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div className="editable" style={{ fontSize: 52, color: "#78AFCE", fontWeight: 800, fontFamily: FONT_HEADING, marginBottom: 16, textTransform: "uppercase", letterSpacing: 3, lineHeight: 1.3 }}><span style={{ fontFamily: FONT_BODY, fontWeight: 700 }}>10-</span>Year<br/>Treasury<br/>Yield</div>
                <div style={{ fontSize: 100, fontWeight: 800, color: "#ffffff", lineHeight: 1, fontFamily: FONT_BODY, fontFeatureSettings: FONT_FEATURE_LNUM }}>{treasLatest != null ? treasLatest.toFixed(2) + "%" : "--"}</div>
                <div style={{ marginBottom: 12 }} />
                <ComparisonText pct={treasChange} text="compared to last week" fontSize={26} />
              </div>
            </div>
            <div style={{ height: 700, color: "#fff" }}>
              <ResponsiveContainer width="100%" height={700}>
                <AreaChart data={chartData} margin={{ top: 30, right: 10, left: 0, bottom: 80 }}>
                  <defs>
                    <linearGradient id="fill_mortgage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E8248D" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#E8248D" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="fill_treasury" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#78AFCE" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#78AFCE" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="x"
                    tick={ratesTick}
                    axisLine={{ stroke: "#ffffff", strokeWidth: 1 }}
                    tickLine={false}
                    interval={0}
                    padding={{ left: 0, right: 0 }}
                  />
                  <YAxis tick={YTick} axisLine={false} tickLine={false} domain={[0, "auto"]} tickFormatter={function(v) { return v + "%"; }} width={80} />
                  <CartesianGrid horizontal={true} vertical={false} stroke={C.gridLine} />
                  <Area type="linear" dataKey="mortgage" stroke="#E8248D" strokeWidth={4} fill="url(#fill_mortgage)" dot={false} />
                  <Area type="linear" dataKey="treasury" stroke="#78AFCE" strokeWidth={4} fill="url(#fill_treasury)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 40, marginTop: 10 }}>
              <span style={{ fontSize: 24, color: "#E8248D", fontFamily: FONT_BODY }}>● 30-Yr Mortgage</span>
              <span style={{ fontSize: 24, color: "#78AFCE", fontFamily: FONT_BODY }}>● 10-Yr Treasury</span>
            </div>
            <div className="editable" style={S.sourceLabel}>Source: FRED / Freddie Mac / U.S. Treasury</div>
          </div>
        );
      })()}

      {/* ═══ FOOTER ═══ */}
      <div style={{ textAlign: "center", padding: "60px 40px 100px", fontFamily: FONT_BODY, fontSize: 24, color: C.dim }}>
        <p className="editable" style={{ margin: 0 }}>
          Source:{" "}
          <a href="https://marketproof.com" style={{ color: C.dim, textDecoration: "underline" }}>Marketproof</a>{" "}
          | For informational purposes only. Data from entities deemed reliable,
          but may contain errors, omissions, or changes without notice.
        </p>
      </div>
    </div>
  );
};

export default Layout;
